import { LoadingOverlay, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { AnimatedRoutedDrawer } from 'src/Components/Drawers/AnimatedRoutedDrawer';
import { InventoryItemDetails } from 'src/Components/Inventory/InventoryItemDetails';
import useFetchInventory, { useFetchInventoryItem } from 'src/Hooks/Queries/useFetchInventory';

type InventoryItemParams = {
  assetId: string;
  itemId: string;
};

const ViewInventoryItemDrawer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { assetId, itemId } = useParams<InventoryItemParams>();
  const { data: item, isLoading, isError } = useFetchInventoryItem(itemId);
  const { data: inventory } = useFetchInventory();
  const inventoryPath = `/asset/${assetId}/inventories`;
  const closeTo = location.pathname.includes('/inventories/exited/')
    ? `${inventoryPath}/exited`
    : inventoryPath;

  if (!itemId) {
    return <Navigate to={closeTo} replace />;
  }

  return (
    <AnimatedRoutedDrawer title={t('inventory.item.title', 'Objet')} to={closeTo}>
      {() => {
        if (isLoading) return <LoadingOverlay visible />;
        if (isError || !item) {
          return <Text>{t('inventory.item.error', 'Impossible de retrouver cet objet.')}</Text>;
        }
        return (
          <InventoryItemDetails
            item={item}
            isManager={inventory?.valuesVisible ?? false}
            rule={
              inventory?.settings ?? { depreciationRatePercent: 10, depreciationMaxPercent: 80 }
            }
            onEdit={() => navigate('edit', { state: { disableAnimation: true }, replace: true })}
            onDeleted={deleted =>
              // The list offers « Annuler » for a few seconds.
              navigate(inventoryPath, {
                replace: true,
                state: { deletedItem: { id: deleted.id, name: deleted.name } },
              })
            }
          />
        );
      }}
    </AnimatedRoutedDrawer>
  );
};

export default ViewInventoryItemDrawer;
